import React, { useState, useEffect } from 'react';

// bootstrap
import { Form, Button } from 'react-bootstrap'

// custom components 
import { CustomSpinner } from '_atoms';
import { AssistanceSelector, CustomModal } from "_molecules";
import BookingNotesSuggestions from './booking-notes-suggestions';

// styles 
import { Colors } from '_styles';

// redux
import { useSelector, useDispatch } from 'react-redux';
import { selectJob } from '_redux/slices/utils/menu-context';
import { resetPostedBooking, selectPatchBookingStatus, selectConfirmBookingError, updateBooking } from '_redux/slices/prm-bookings/bookings';
import { selectBookingType, selectPickUpLocations } from "_redux/slices/timeline/timeline-areas";
import { getBookings, getDepartureBookings, resetUpdateTaskFlags, setBookingLocation } from "_redux/slices/timeline/timeline-data";
import { reset as resetForm } from '_redux/slices/assistance-bookings/booking-form';
import { selectFirebaseUser, selectUserDb } from '_redux/slices/main-screen/user';
import { selectDefaultCheckInLocation } from '_redux/slices/timeline/timeline-control';
import { selectUsedTheme } from '_redux/slices/timeline/theme';

// date 
import moment from 'moment';

// functions 
import { eqUnorderedStringArrays, formatDayTime, getAssistanceArray, getAssistanceString } from '_utils/functions';

// drop down menu
import Select from 'react-select';

// validation
import validator from 'validator';



const EditTimelineBookingForm = (props) => {

    //#region redux
    const dispatch = useDispatch();

    const dbUser = useSelector(selectUserDb)
    const firebaseUser = useSelector(selectFirebaseUser);

    const job = useSelector(selectJob);                                             // selected booking to edit 
    const patchStatus = useSelector(selectPatchBookingStatus)                       // status of updating bookings
    const patchError = useSelector(selectConfirmBookingError);
    const bookingType = useSelector(selectBookingType);                             // select bookingType (departures,arrivals,all)
    const pickUpLocations = useSelector(selectPickUpLocations);                     // pickup locations selector
    const defaultCheckInLocation = useSelector(selectDefaultCheckInLocation);       // default CheckIn Location
    const usedTheme = useSelector(selectUsedTheme);
    //#endregion

    //#region local states
    const [booking, setBooking] = useState(null)                                    // selected booking
    const [firstName, setFirstName] = useState("")                                  // PAX firstName
    const [secondName, setSecondName] = useState("")                                // PAX secondName
    const [assistance, setAssistance] = useState("")                                // PAX assistance
    const [bookingNotes, setBookingNotes] = useState("")                            // Booking Notes
    const [selectedPickUp, setSelectedPickUp] = useState(null);                     // selected pick up location
    const [errorMessage, setError] = useState("");                                  // error message for displaying issues with 
    const [editAssistanceReason, setEditAssistanceReason] = useState(null);
    const [showAddReasonInput, setShowAddReasonInput] = useState(false);
    const [assistanceHistoric, setAssistanceHistoric] = useState([])
    const [originalAssistance, setOriginalAssistance] = useState([]);               // store original assistance once on load
    const [isInitialized, setIsInitialized] = useState(false);                      // track if form has been initialized
    //#endregion
    const [preboardAssistanceRequired, setPreboardAssistanceRequired] = useState(true);     // default to true
    const [boardingAssistNeeded, setBoardingAssistNeeded] = useState(true);
    const [selectedBoardingPickup, setSelectedBoardingPickup] = useState(null);             // boarding pickup location when preboard not required
    const [passengerEmail, setPassengerEmail] = useState("");       // optional, makes non-app bookings eligible for the email survey

    const trimmedEmail = props?.forCheckIn ? passengerEmail.trim() : "";
    const emailError = trimmedEmail.length > 0 && !validator.isEmail(trimmedEmail);
    //#region useEffect

    // reset on first render 
    useEffect(() => {
        dispatch(resetPostedBooking())
        dispatch(resetUpdateTaskFlags())
        setPickUpLocation(null);
    }, []);

    // get the original booking doc 
    useEffect(() => {
        if (!Object.isObjectEmpty(job)) {
            setBooking(job?.originalBookingDoc ? job?.originalBookingDoc : job)
        }
    }, [job])

    // set the passenger name and assistance by the booking - ONLY on initial load
    useEffect(() => {
        if (!Object.isObjectEmpty(booking) && !isInitialized) {
            setFirstName(booking.name?.split(" ")?.[0] ?? "")
            setSecondName(booking.name?.split(" ")?.[1] ?? "")
            const tempAssistance = getAssistanceArray(booking.assistance ?? [])

            const hist = Array.isArray(booking?.editAssistanceHistoric)
                ? booking.editAssistanceHistoric
                : [];

            setAssistanceHistoric(hist);
            setBookingNotes(booking.notes ?? "")
            setAssistance(tempAssistance)
            setOriginalAssistance(tempAssistance)  // Store original assistance for comparison

            if (booking?.preboardAssistanceRequired === false) {
                setPreboardAssistanceRequired(false)
            }
            setBoardingAssistNeeded(booking?.boardingAssistNeeded === false ? false : true)

            // Initialize boarding pickup location if it exists
            if (booking?.boardingPickupLocation?.id) {
                setSelectedBoardingPickup({
                    value: booking.boardingPickupLocation.id,
                    label: booking.boardingPickupLocation.name
                });
            } else {
                setSelectedBoardingPickup(null);
            }
            setPassengerEmail(booking?.passengerEmail ?? "")

            setIsInitialized(true);  // Mark as initialized to prevent re-runs
        }
    }, [booking, isInitialized])

    // Handle pickup location separately - this can update when dependencies change
    useEffect(() => {
        if (!Object.isObjectEmpty(booking) && pickUpLocations?.length > 0) {
            // Only set pickup location if not already set by user
            if (!selectedPickUp) {
                if (booking.paxPositionInfo?.activity === "Checked-in" && booking.paxPositionInfo?.locationId) {
                    setPickUpLocation(booking.paxPositionInfo?.locationId);
                } else if (defaultCheckInLocation) {
                    setPickUpLocation(defaultCheckInLocation);
                }
            }
        }
    }, [booking, defaultCheckInLocation, pickUpLocations])

    // NOTE FOR GHADER TO CHANGE
    // monitor patch status
    useEffect(() => {
        if (patchStatus == 'succeeded') {

            if (props?.forCheckIn && selectedPickUp?.value) {
                let object = {
                    bookingId: booking?._id,
                    location: selectedPickUp?.value,
                    locationTitle: selectedPickUp?.label,
                    activity: "Checked-in",
                    time: new Date(),
                    preboardAssistanceRequired: preboardAssistanceRequired,
                    ...(trimmedEmail && { passengerEmail: trimmedEmail.toLowerCase() }),
                    // forFireStore: true,
                    setBy: {
                        id: firebaseUser?.uid ?? "",
                        name: firebaseUser?.displayName ?? "",
                        from: "portal",
                        time: new Date(),
                    }
                };
                dispatch(setBookingLocation(object))
            }


            if (bookingType?.includes("departures") || bookingType?.includes("all")) {
                handleGetDeparturesBookings();
            }
            if (bookingType?.includes("arrivals") || bookingType?.includes("all")) {
                handleGetArrivalsBookings();
            }

            // show success modal
            dispatch(resetForm());
            dispatch(resetPostedBooking());
            props?.close();
        }
        if (patchStatus == 'failed') {
            // show error messaging
            setError('Error updating booking, please contact admin support at timeline-support@prmassist.com.');
        }
    }, [patchStatus]);



    // set default pickup location if its checkin modal
    useEffect(() => {
        if (defaultCheckInLocation) {
            setPickUpLocation(defaultCheckInLocation);
        };
    }, [defaultCheckInLocation]);


    //#endregion


    //#region fucntions 

    // get todays arrivals bookings
    const handleGetArrivalsBookings = () => {
        const today = moment(new Date()).format().split("T")[0];
        dispatch(
            getBookings({
                iata: dbUser.details.authority,
                type: "inbound",
                start: today,
                end: today,
                skip: 0,
                limit: 1000,
                source: "none",
            })
        );
    };

    // get todays departures bookings
    const handleGetDeparturesBookings = () => {
        const today = moment(new Date()).format().split("T")[0];

        dispatch(
            getDepartureBookings({
                iata: dbUser.details.authority,
                type: "outbound",
                start: today,
                end: today,
                skip: 0,
                limit: 1000,
                source: "none",
            })
        );
    };

    const editBooking = () => {
        // clear error
        setError("");

        if (Object.isObjectEmpty(booking)) return;

        const assistanceChanged = !eqUnorderedStringArrays(assistance, originalAssistance);

        // Build history entry if changed
        let editAssistanceHistoric = booking.editAssistanceHistoric || [];
        if (assistanceChanged) {
            //extract author info from Firebase user
            const author = {
                id: firebaseUser?.uid || firebaseUser?.id || "unknown",
                name:
                    firebaseUser?.displayName ||
                    firebaseUser?.name ||
                    firebaseUser?.email ||
                    "Unknown",
            };

            editAssistanceHistoric = [
                ...editAssistanceHistoric,
                {
                    prev: originalAssistance,       // array of codes before change (from state)
                    next: assistance,               // array of codes after change
                    reason: editAssistanceReason?.trim() || "",
                    author,
                    editedAt: new Date().toISOString(),
                },
            ];
        }

        // Build the doc to patch
        let newBookingDoc = {
            name: `${firstName} ${secondName}`.trim(),
            assistance,
            notes: bookingNotes,
            ...(assistanceChanged ? { editAssistanceHistoric } : {}),

            preboardAssistanceRequired: preboardAssistanceRequired ? true : false,
            selfAssisted: !preboardAssistanceRequired ? true : false,
            boardingAssistNeeded: boardingAssistNeeded ? true : false,

            // add passenger boarding pickup location (only when preboard not required but boarding is required)
            boardingPickupLocation: (!preboardAssistanceRequired && boardingAssistNeeded && selectedBoardingPickup?.value) ? {
                id: selectedBoardingPickup.value,
                name: selectedBoardingPickup.label,
            } : null,


        };

        if (props?.forCheckIn && selectedPickUp?.value) {
            newBookingDoc = {
                ...newBookingDoc,
                paxPositionInfo: {
                    activity: "Checked-in",
                    locationId: selectedPickUp.value,
                    locationTitle: selectedPickUp.label,
                    time: new Date(),
                },
            };
        }
        const payload = {
            id: booking._id || booking.id,
            body: {
                ...newBookingDoc
            }
        }

        dispatch(updateBooking(payload));
    };

    const setPickUpLocation = (value) => {
        if (!value) {
            setSelectedPickUp(null);
        } else {
            const selectedLocation = pickUpLocations?.find((loc) => loc._id === value);
            if (selectedLocation) {
                setSelectedPickUp({
                    value: selectedLocation?._id ?? "",
                    label: selectedLocation?.title ?? ""
                });
            } else setSelectedPickUp(null);

        }
    }
    const handleChangeAssistance = (codes) => {
        const normalized = Array.isArray(codes) ? codes : getAssistanceArray(codes);
        // Only check for changes if form is initialized (originalAssistance is set)
        if (isInitialized && originalAssistance.length > 0) {
            const changed = !eqUnorderedStringArrays(normalized, originalAssistance);
            setShowAddReasonInput(changed);
            if (!changed) setEditAssistanceReason(""); // clear if not needed
        }
        setAssistance(normalized);
    };

    //#endregion

    return (
        <>
            <style>
                {`
                    .form-input::placeholder {
                    color: ${usedTheme.TEXT_COLOR} !important;
                    opacity: 1;
                    }
                `}
            </style>
            <Form>
                <div style={{ display: "grid" }}>
                    {/* Passenger Name */}
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ width: "45%" }}>
                            <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>First Name</Form.Label>
                            <Form.Control
                                className="form-input"
                                type="text"
                                placeholder="Enter first name"
                                onChange={(e) => setFirstName(e.target.value)}
                                value={firstName}
                                disabled={true}
                                style={{ backgroundColor: usedTheme.BACKGROUND_COLOR, borderColor: usedTheme.BORDER_COLOR, color: usedTheme.TEXT_COLOR }}
                            />
                        </div>
                        <div style={{ width: "45%" }}>
                            <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Last Name</Form.Label>
                            <Form.Control
                                className="form-input"
                                type="text"
                                placeholder="Enter last name"
                                onChange={(e) => setSecondName(e.target.value)}
                                value={secondName}
                                disabled={true}
                                style={{ backgroundColor: usedTheme.BACKGROUND_COLOR, borderColor: usedTheme.BORDER_COLOR, color: usedTheme.TEXT_COLOR }}
                            />
                        </div>
                    </div>

                    {/* Passenger Email - check-in only, saved by setBookingLocation */}
                    {props?.forCheckIn && (
                        <div className="mt-3">
                            <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Passenger Email (Optional)</Form.Label>
                            <Form.Control
                                className="form-input"
                                type="email"
                                placeholder="name@example.com"
                                onChange={(e) => setPassengerEmail(e.target.value)}
                                value={passengerEmail}
                                isInvalid={emailError}
                                style={{ backgroundColor: usedTheme.BACKGROUND_COLOR, borderColor: emailError ? undefined : usedTheme.BORDER_COLOR, color: usedTheme.TEXT_COLOR }}
                            />
                            <Form.Text style={{ color: emailError ? Colors.DARK_RED : usedTheme.TEXT_COLOR, opacity: emailError ? 1 : 0.7 }}>
                                {emailError
                                    ? "Enter a valid email address or leave it blank"
                                    : "Only with the passenger's agreement. Used to send them a short feedback survey after their journey."}
                            </Form.Text>
                        </div>
                    )}

                    {/* Assistance Selector */}
                    <div className="mt-4">
                        {assistanceHistoric.length > 0 && (
                            <div className="mb-2" style={{ padding: "8px", border: `1px solid ${usedTheme.BORDER_COLOR}`, borderRadius: 8 }}>
                                <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE, marginBottom: 6 }}>
                                    Assistance Edit History
                                </Form.Label>

                                {assistanceHistoric.map((h, i) => (
                                    <div key={i} style={{ padding: "8px 0", borderTop: i ? `1px solid ${usedTheme.BORDER_COLOR}` : "none" }}>
                                        <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}>
                                            Edited by <strong>{h?.author.name || "Unknown"}</strong> At:{formatDayTime(h?.editedAt)}
                                        </div>
                                        <div style={{ fontSize: 13 }}>
                                            <div><strong>Old:</strong> {getAssistanceString(h?.prev)}</div>
                                            <div><strong>New:</strong> {getAssistanceString(h?.next)}</div>
                                            <div style={{ marginTop: 4 }}>
                                                <strong>Reason:</strong> {h?.reason || "—"}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Assistance Requirements</Form.Label>
                        <AssistanceSelector
                            setAssistance={handleChangeAssistance}
                            defaultValue={assistance}
                            value={assistance}
                        />
                        {showAddReasonInput && <Form.Control
                            className="form-input"
                            as="textarea"
                            type="text"
                            placeholder="Add reason for editing assistance (Optional)"
                            onChange={(e) => setEditAssistanceReason(e.target.value)}
                            value={editAssistanceReason || ""}
                            style={{ height: '60px', marginTop: "4px", backgroundColor: usedTheme.BACKGROUND_COLOR, borderColor: usedTheme.BORDER_COLOR, color: usedTheme.TEXT_COLOR }}
                        />}

                    </div>
                    {props?.forCheckIn && booking?.flightConfirmationDetails?.origin == dbUser?.details?.authority && (
                        <div>

                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>

                                {/* self assisted passenger */}
                                <div className="mt-4" style={{}}>
                                    <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Preboard Assistance Required</Form.Label>
                                    <Form.Check
                                        onClick={() => setPreboardAssistanceRequired(!preboardAssistanceRequired)}
                                        type="switch"
                                        defaultChecked={preboardAssistanceRequired}
                                        style={{ marginLeft: "16px" }}
                                        checked={preboardAssistanceRequired}
                                    />
                                </div>

                                {/* boarding assistance needed */}
                                <div className="mt-4" style={{}}>
                                    <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Boarding Assistance Required</Form.Label>
                                    <Form.Check
                                        onClick={() => setBoardingAssistNeeded(!boardingAssistNeeded)}
                                        type="switch"
                                        defaultChecked={boardingAssistNeeded}
                                        style={{ marginLeft: "16px" }}
                                        checked={boardingAssistNeeded}
                                    />

                                </div>

                            </div>

                            {!boardingAssistNeeded && <p style={{ color: "red", marginTop: "4px" }}> Please note that if Boarding Assistance Needed is not switched on, this PAX will be marked as "No Boarding Assistance Needed".</p>}

                            {/* Boarding Pickup Location - shown when preboard assistance is NOT required but boarding assistance IS required */}
                            {!preboardAssistanceRequired && boardingAssistNeeded && (
                                <div className='mt-4 d-grid'>
                                    <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Pickup Location <span style={{ color: 'red' }}>*</span></Form.Label>
                                    <p style={{ fontSize: '12px', color: usedTheme.TEXT_COLOR, opacity: 0.8, marginBottom: '8px' }}>
                                        This will be the pickup location for the passenger for boarding assistance.
                                    </p>
                                    <Select
                                        value={selectedBoardingPickup}
                                        onChange={(selectedOption) => {
                                            setSelectedBoardingPickup(selectedOption);
                                        }}
                                        options={pickUpLocations?.map(loc => ({
                                            value: loc._id,
                                            label: loc.title
                                        }))}
                                        placeholder="Choose Pickup Location..."
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minWidth: "25%",
                                                borderRadius: "5px",
                                                backgroundColor: usedTheme.BACKGROUND_COLOR,
                                                borderColor: usedTheme.BORDER_COLOR,
                                                color: usedTheme.TEXT_COLOR,
                                            }),
                                            placeholder: (provided) => ({
                                                ...provided,
                                                color: usedTheme.TEXT_COLOR,
                                            }),
                                            singleValue: (provided) => ({
                                                ...provided,
                                                color: usedTheme.TEXT_COLOR,
                                            }),
                                            menu: (provided) => ({
                                                ...provided,
                                                backgroundColor: usedTheme.BACKGROUND_COLOR,
                                                borderColor: usedTheme.BORDER_COLOR,
                                                color: usedTheme.TEXT_COLOR
                                            }),
                                            option: (provided, state) => ({
                                                ...provided,
                                                backgroundColor: state.isFocused
                                                    ? usedTheme.BORDER_COLOR
                                                    : usedTheme.BACKGROUND_COLOR,
                                                color: usedTheme.TEXT_COLOR,
                                                cursor: 'pointer',
                                            }),
                                        }}
                                        isClearable
                                    />
                                </div>
                            )}

                            <div className='mt-4 d-grid'>
                                <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>{props?.locationSelectTitle ? props?.locationSelectTitle : "Pick Up Location (Optional)"}</Form.Label>
                                <Select
                                    value={selectedPickUp}
                                    onChange={(selectedOption) => {
                                        setSelectedPickUp(selectedOption);
                                        setPickUpLocation(selectedOption?.value);
                                    }}
                                    options={pickUpLocations?.map(loc => ({
                                        value: loc._id,
                                        label: loc.title
                                    }))}
                                    placeholder={props?.placeholder ? props?.placeholder : "Choose Pick Up Location..."}
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minWidth: "25%",
                                            borderRadius: "5px",
                                            backgroundColor: usedTheme.BACKGROUND_COLOR,
                                            borderColor: usedTheme.BORDER_COLOR,
                                            color: usedTheme.TEXT_COLOR,
                                        }),
                                        placeholder: (provided) => ({
                                            ...provided,
                                            color: usedTheme.TEXT_COLOR,
                                        }),
                                        singleValue: (provided) => ({
                                            ...provided,
                                            color: usedTheme.TEXT_COLOR,
                                        }),
                                        menu: (provided) => ({
                                            ...provided,
                                            backgroundColor: usedTheme.BACKGROUND_COLOR,
                                            borderColor: usedTheme.BORDER_COLOR,
                                            color: usedTheme.TEXT_COLOR
                                        }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused
                                                ? usedTheme.BORDER_COLOR
                                                : usedTheme.BACKGROUND_COLOR,
                                            color: usedTheme.TEXT_COLOR,
                                            cursor: 'pointer',
                                        }),
                                    }}
                                    isClearable
                                />
                            </div>

                        </div>
                    )
                    }

                    {/* Booking Notes */}
                    <div className="mt-3">
                        <BookingNotesSuggestions
                            setBookingNotes={setBookingNotes}
                            bookingNotes={bookingNotes}
                        />
                    </div>

                    {/* Save Button */}
                    <div className="mt-4" style={{ display: "flex", justifyContent: "center" }}>
                        <Button
                            variant="outline-primary"
                            onClick={editBooking}
                            style={{ width: 200 }}
                            disabled={!firstName?.length || !secondName?.length || !assistance?.length || emailError || (!preboardAssistanceRequired && boardingAssistNeeded && !selectedBoardingPickup?.value)}
                        >
                            {patchStatus == 'loading' ? <CustomSpinner variant="light" size={"small"} /> : props?.btnTitle ? props?.btnTitle : 'Save Booking'}
                        </Button>
                    </div>

                    {/* error message */}
                    {errorMessage && (
                        <div className="mt-3" style={{ color: Colors.RED, textAlign: "center", color: 'red' }}>
                            {errorMessage}
                        </div>
                    )}

                </div>
            </Form>

        </>
    )
}

export default EditTimelineBookingForm;